import React from 'react';
import LegalLayout from './LegalLayout';
import LegalText from './LegalText';

export const TERMS_TEXT = `
This ManageLift B2B SaaS Customer Agreement (the “Agreement”) is between ManageLift LLC (“ManageLift,” “we,” “us,” or “our”) and the business or organization accepting this Agreement (“Customer,” “you,” or “your”). This Agreement governs Customer’s business use of the ManageLift software service.

1. Agreement and Acceptance

By checking the applicable acceptance box, starting a ManageLift subscription, or otherwise affirmatively accepting this Agreement through ManageLift’s supported signup or Checkout process, the person accepting this Agreement:

1.   agrees to this Agreement on behalf of Customer; and

2.   represents that the person has authority to bind Customer.

If that person does not have authority to bind Customer, the person must not accept this Agreement on Customer’s behalf.

The subscription plan, billing interval, quantity, and pricing presented during Checkout form part of this Agreement. ManageLift is offered for business use and is not intended as a consumer product.

2. The ManageLift Service

ManageLift is software for forklift fleet maintenance and inspection operations. Depending on the features currently available to Customer, the Service may support:

forklift fleet records

operator inspections and checklists

maintenance records

repair records

technicians and operators

customer user roles

attachments

submitted service records

printable or downloadable records

maintenance-cycle and fleet-status workflows

subscription billing

ManageLift is a software recordkeeping and workflow tool. It is not a full CMMS or EAM system unless ManageLift expressly identifies particular functionality as such. ManageLift may update and improve the Service over time as described in this Agreement.

3. Customer Account and Authorized Users

Account Owner

Each Customer account has one or more users with applicable Account Owner authority under the Service. The Account Owner is responsible for account-level matters that the Service designates for Account Owner control, including subscription and billing management.

Other Users

Customer may authorize additional personnel to use the Service in supported roles, which may include Fleet Managers, Operators, and Technicians.

Customer is responsible for:

deciding who should receive access

ensuring invited users are authorized to act for Customer in their assigned roles

promptly removing or changing access when personnel no longer require it

maintaining accurate company and account information

activity conducted through Customer’s authorized accounts, except to the extent caused by ManageLift’s own breach of this Agreement or security failure

Customer and its users must protect their credentials and must not share credentials in a manner that defeats the Service’s intended user-access controls. Customer should promptly notify ManageLift at support@managelift.com if Customer knows or reasonably suspects that an account has been compromised. ManageLift personnel do not require Customer users to provide their passwords and do not routinely impersonate Customer users to provide the Service.

4. Subscription, Pricing, Trial, and Billing

Current Launch Pricing

Unless different pricing is expressly shown and accepted during Checkout:

Monthly Plan: $5.99 per active, non-retired forklift per month.

Annual Plan: $59.99 per forklift per year.

Stripe processes subscription payments. The pricing, billing interval, and subscription quantity presented during Checkout are incorporated into Customer’s subscription.

90-Day Trial

A new qualifying subscription includes a 90-day trial. Unless Customer cancels before the applicable trial ends, the subscription automatically transitions to paid service and the payment processor may attempt the first applicable subscription charge. Cancellation during the trial prevents future paid renewal or charging in accordance with the cancellation state configured for that subscription. Unless otherwise shown when cancellation is requested, Customer may continue using the Service through the applicable trial or cancellation effective date.

Automatic Renewal

After the trial, paid subscriptions continue and renew at their applicable monthly or annual billing interval unless canceled in accordance with this Agreement.

Monthly Forklift Quantity

Monthly subscription quantity is based on Customer’s active, non-retired forklifts. The Service may synchronize the monthly subscription quantity as eligible forklifts are added, retired, or restored. Ordinary monthly quantity synchronization does not automatically create prorated charges or credits for the partial billing period. The updated quantity may affect subsequent billing.

Annual Forklift Quantity

The annual subscription begins with the applicable forklift quantity established at Checkout. Annual quantity is not continuously and automatically synchronized in the same manner as monthly quantity. If Customer adds or reactivates forklifts during a paid annual term, ManageLift may charge the applicable remaining-term amount for those additional forklifts based on the annual unit price and remaining service period. Retiring a forklift during an already-paid annual term does not automatically create a prorated refund or credit. ManageLift may reconcile eligible forklift quantity before an annual renewal.

Payment Issues

If a payment fails or a subscription otherwise enters a payment-problem state, ManageLift may temporarily continue Service access while the payment issue is being resolved. Temporary continued access is not a contractual right to indefinite unpaid use. ManageLift may restrict or suspend access when the applicable entitlement or payment-grace period ends. Access may resume after the subscription and payment return to good standing. A failed payment does not by itself automatically delete Customer’s operational records.

Refunds and Credits

Except where required by applicable law or expressly stated otherwise:

subscription payments are generally non-refundable

cancellation does not automatically create a prorated refund or credit for unused time

retirement of forklifts during a paid annual term does not automatically create a refund or credit

Customer remains responsible for amounts properly incurred before cancellation becomes effective

ManageLift may correct billing mistakes and may issue refunds or credits when ManageLift determines that doing so is appropriate.

5. Cancellation, Suspension, and Termination

Customer Cancellation

An Account Owner may manage subscription cancellation through the Stripe Customer Portal made available through ManageLift. Unless otherwise expressly stated during the cancellation process, cancellation becomes effective at the end of the applicable current trial or paid billing period. Normal Service access may continue until that effective date. When Customer’s paid entitlement ends, normal paid-feature access ends. Cancellation does not automatically erase Customer’s operational history.

Suspension by ManageLift

ManageLift may suspend some or all access when reasonably necessary because of:

nonpayment

a material violation of this Agreement

unlawful use

a security threat or suspected compromise

conduct that creates material risk to the Service or another customer

abuse of the Service

a legal or regulatory requirement

Where reasonably practical under the circumstances, ManageLift will attempt to give Customer notice and an opportunity to address the problem before suspension. Immediate action may be taken when reasonably necessary to protect Customer, ManageLift, other customers, or the Service.

Termination for Cause

Either party may terminate this Agreement for a material breach that remains uncured after reasonable notice and an opportunity to cure when cure is reasonably possible. ManageLift may terminate immediately for serious unlawful activity, intentional security abuse, fraud, or conduct creating a substantial threat to the Service or other customers.

ManageLift Termination Without Cause

ManageLift may terminate a paid Customer subscription without cause upon at least 30 days’ notice. If ManageLift terminates a prepaid subscription without cause before the end of the prepaid period, ManageLift will refund the portion of prepaid subscription fees attributable to the period after termination. This does not apply to termination resulting from Customer’s breach, nonpayment, unlawful activity, or other termination for cause.

6. Customer Data and Privacy

“Customer Data” means information, records, files, attachments, submitted forms, operational data, and other content Customer or its Authorized Users submit to the Service.

Ownership

As between Customer and ManageLift, Customer retains its rights in Customer Data. ManageLift does not acquire ownership of Customer’s forklift, inspection, maintenance, repair, personnel, or other operational records merely because they are stored in ManageLift.

Permission to Process Customer Data

Customer grants ManageLift the limited rights reasonably necessary to host, store, process, transmit, back up, display, secure, troubleshoot, and otherwise handle Customer Data for the purpose of operating, protecting, maintaining, and supporting the Service and complying with applicable law. Customer represents that it has the rights and authority reasonably necessary to provide Customer Data to ManageLift and to instruct ManageLift to process it for these purposes.

Personal Information

ManageLift’s handling of personal information is also described in the ManageLift Privacy Notice at /privacy. The Privacy Notice is separate from this Agreement.

7. Data Retention and Customer Records

Cancellation or expiration of a subscription does not automatically delete Customer’s operational history. ManageLift may retain Customer Data following cancellation or expiration for legitimate operational, business-record, security, backup, dispute-resolution, legal-compliance, and record-preservation purposes, subject to applicable law and ManageLift’s then-current retention practices.

This Agreement does not promise indefinite retention, a specific deletion timetable, or a specific backup-deletion timetable. Customer may contact support@managelift.com with reasonable questions concerning Customer Data or post-cancellation access.

Record Access and Export

While Customer has applicable Service access, Customer may use the printing, viewing, downloading, and other record-access capabilities then supported by ManageLift. ManageLift does not promise a bulk database export, a database dump, a particular export format, custom migration services, or a guaranteed turnaround time for special data requests unless ManageLift separately agrees to provide such a service. Customer is responsible for retaining independent copies of any records that Customer determines it must preserve independently because of law, company policy, insurance requirements, contract requirements, or safety programs.

8. Customer Responsibilities

Customer is responsible for its own operations and for decisions made using information stored or displayed in ManageLift. Customer is responsible for:

establishing its inspection procedures

maintaining its equipment

determining appropriate service and repair practices

employing or engaging appropriately qualified personnel

training and supervising its personnel

maintaining accurate records entered into the Service

determining whether equipment should be placed into or removed from service

maintaining required workplace safety programs

complying with laws and regulations applicable to Customer’s operations

Customer must independently evaluate information entered by its personnel. ManageLift does not independently verify the factual accuracy of Customer’s submitted operational records.

9. Forklift Safety and Regulatory Responsibility

This section is material to the parties’ allocation of responsibility.

ManageLift is software. ManageLift does not operate, inspect, maintain, repair, certify, supervise, or control Customer’s forklifts or workplace.

Customer remains solely responsible for:

workplace safety

determining when and how often equipment must be inspected

deciding whether a forklift or other equipment is safe to operate

removing unsafe equipment from service

maintenance and repair decisions

selecting qualified maintenance and service personnel

operator training, evaluation, authorization, and supervision

equipment modifications

workplace-specific safety procedures

compliance with OSHA and all other laws, regulations, standards, contractual requirements, and internal policies applicable to Customer

ManageLift does not certify a forklift as safe; provide OSHA certification; provide legal or workplace-safety advice; guarantee regulatory compliance; determine whether a specific inspection satisfies a legal requirement; or guarantee that a record entered into ManageLift proves regulatory compliance.

Electronic checklists and other records stored in ManageLift are tools for Customer’s workflow and recordkeeping. Their existence alone does not establish that Customer has complied with any particular safety, regulatory, contractual, or evidentiary requirement. Customer is responsible for deciding whether ManageLift’s forms, records, workflows, signatures, and retention practices are suitable for Customer’s specific obligations.

Submitted Names and Signatures

ManageLift may permit users to submit names, signatures, acknowledgments, or similar record information. Unless ManageLift expressly states otherwise, such functionality is not notarization, biometric verification, independent identity verification, government-issued digital-signature certification, or a guarantee that a particular signature satisfies every legal or regulatory requirement.

10. Acceptable Use

Customer and its Authorized Users must not:

use the Service unlawfully

access or attempt to access another customer’s data without authorization

bypass or attempt to bypass authentication, role, tenant, or other security controls

introduce malware or malicious code

interfere with the operation or security of the Service

perform abusive automated access or scraping

probe or exploit the Service except through an expressly authorized security-testing arrangement

use another person’s credentials without authorization

use the Service in a manner that infringes or violates another person’s rights

ManageLift may take reasonable action to investigate suspected violations and protect the Service.

11. Intellectual Property

ManageLift and its licensors retain all rights in and to the Service, including its software, source code and object code, user interface, branding, documentation, workflows, service design, and underlying technology, except for Customer Data and other rights expressly granted to Customer.

Subject to this Agreement, ManageLift grants Customer a limited, non-exclusive, non-transferable right to use the Service during Customer’s applicable subscription for Customer’s internal business operations. Customer may provide suggestions or feedback concerning ManageLift. ManageLift may use that feedback to improve its products and services without owing compensation or ownership rights to Customer, provided that doing so does not transfer ownership of Customer Data to ManageLift.

12. Confidentiality

Each party may receive nonpublic information from the other party that a reasonable business person would understand to be confidential. Confidential information may include nonpublic business information, Customer Data, credentials, security information, pricing arrangements not publicly offered, and technical information.

The receiving party will use confidential information only as reasonably necessary for the relationship under this Agreement and use reasonable care to protect it from unauthorized disclosure.

Confidential information does not include information the receiving party can demonstrate was already lawfully known without confidentiality restrictions, becomes public without breach of this Agreement, is lawfully received from another source without confidentiality restrictions, or is independently developed without use of the other party’s confidential information. A party may disclose confidential information when legally required, provided that legally permitted notice is given to the other party when reasonably practical.

13. Third-Party Services

ManageLift relies on third-party services to provide portions of the Service. Subscription payment processing is handled through Stripe. Customer’s use of Stripe-hosted payment or billing functionality may also be subject to Stripe’s applicable terms and privacy practices. ManageLift may use other hosting, authentication, storage, communications, monitoring, or infrastructure providers. ManageLift remains responsible for its obligations under this Agreement but cannot guarantee uninterrupted operation of independent third-party services.

14. Service Availability, Security, Backups, and Changes

Availability

ManageLift will use reasonable efforts to operate and maintain the Service. However, ManageLift does not offer a formal service-level agreement under this Agreement and does not promise 100% availability, uninterrupted service, zero downtime, a particular recovery-time objective, or a particular recovery-point objective. The Service may be unavailable because of maintenance, updates, third-party provider outages, Internet failures, emergencies, security actions, or circumstances outside ManageLift’s reasonable control.

Security

ManageLift will maintain reasonable administrative, technical, and organizational safeguards appropriate to the nature of the Service and information it processes. No Internet-connected system can be guaranteed to be completely secure, and ManageLift does not make such a guarantee. Customer is responsible for reasonable security practices within Customer’s own organization, including credential management and promptly removing access no longer required.

Backups and Data Loss

ManageLift may use backup and recovery measures as part of operating the Service, but does not guarantee that data loss can never occur or that every item can always be recovered. Customer should separately preserve records that it independently determines are critical or legally required.

Service Changes

ManageLift may modify, improve, replace, or discontinue features as the Service evolves. ManageLift will use reasonable efforts to avoid materially disrupting a paid Customer’s core use of the Service without reasonable notice when practical. Nothing in this section requires ManageLift to delay a change necessary for security, legal compliance, emergency response, or prevention of harm.

15. Disclaimers

Except for obligations expressly stated in this Agreement and to the fullest extent permitted by law, the Service is provided on an “as available” basis.

ManageLift does not warrant that the Service will be uninterrupted or error-free; all Customer Data can never be lost; the Service will satisfy every Customer-specific legal, regulatory, insurance, contractual, or internal-policy requirement; use of the Service will make Customer compliant with OSHA or any other law; a forklift shown in a particular state or status is physically safe to operate; Customer’s personnel have performed an inspection correctly; or Customer’s records will be accepted for every legal or evidentiary purpose.

To the fullest extent permitted by applicable law, ManageLift disclaims implied warranties, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. Nothing in this Agreement excludes a warranty or obligation that applicable law does not permit the parties to exclude.

16. Limitation of Liability

To the fullest extent permitted by applicable law, neither party will be liable to the other for indirect, incidental, special, exemplary, punitive, or consequential damages arising from or relating to this Agreement or the Service, including lost profits, lost revenue, or loss of business opportunity, even if advised that such damages were possible.

To the fullest extent permitted by applicable law, ManageLift’s total aggregate liability arising from or relating to this Agreement or the Service will not exceed the greater of:

1.   the subscription fees paid by Customer to ManageLift during the 12 months immediately preceding the event giving rise to the claim; or

2.   $1,000.

This limitation does not limit Customer’s obligation to pay amounts properly due under the Agreement and does not apply to liability that applicable law does not permit the parties to limit. The parties acknowledge that the subscription pricing reflects this allocation of risk.

17. Narrow Customer Indemnification

Customer will defend and indemnify ManageLift against a third-party claim to the extent the claim results from:

1.   Customer Data that Customer did not have the lawful right to submit or use;

2.   Customer’s unlawful use of the Service; or

3.   Customer’s intentional unauthorized access to another person’s systems or data through misuse of the Service.

Customer has no obligation under this section to the extent a claim was caused by ManageLift’s own breach of this Agreement, unlawful conduct, or gross negligence or willful misconduct. ManageLift will provide reasonable notice of an indemnified claim and reasonable cooperation in its defense.

18. Governing Law and Disputes

This Agreement is governed by the laws of the State of Indiana, without regard to its conflict-of-law rules.

Subject to applicable law, the parties consent to exclusive jurisdiction and venue in the state courts located in Marion County, Indiana, and the federal court having jurisdiction over that location.

The parties are not agreeing in this Agreement to mandatory arbitration or a class-action waiver. Before filing litigation, the parties are encouraged, but not required, to attempt in good faith to resolve an ordinary business dispute through direct discussion.

19. Changes to This Agreement

ManageLift may update this Agreement as the Service and business evolve. If a change is material to an existing Customer’s rights or obligations, ManageLift will provide reasonable advance notice through email, the Service, or another reasonable account communication method before the material change becomes effective, except where a faster change is reasonably required by law, security concerns, or emergency circumstances.

Changes will not retroactively alter fees or obligations already accrued before their effective date unless the parties expressly agree or applicable law requires otherwise. Continued use after an updated Agreement becomes effective may constitute acceptance where permitted by applicable law.

20. Electronic Communications and Notices

Customer agrees that ManageLift may send account, billing, security, support, contractual, and Service communications electronically to the Account Owner or other contact information Customer provides. Customer is responsible for maintaining accurate contact information.

Routine support and account questions may be sent to support@managelift.com.

Legal notices to ManageLift may be sent to: ManageLift LLC, 4335 Stone Mill Dr., Indianapolis, IN 46237, and support@managelift.com.

A notice to Customer may be sent to the Account Owner or primary business contact associated with Customer’s account.

21. General Terms

Assignment

Customer may not assign this Agreement without ManageLift’s prior written consent, which will not be unreasonably withheld for a legitimate business reorganization or transfer. ManageLift may assign this Agreement in connection with a merger, acquisition, financing, corporate reorganization, or sale of all or substantially all of the business or assets relating to ManageLift.

Force Majeure

Neither party is liable for delay or failure caused by events beyond its reasonable control, except that this section does not excuse Customer from payment obligations already due.

No Waiver

Failure to enforce a provision on one occasion does not waive the right to enforce that provision later.

Severability

If a provision is held unenforceable, the remaining provisions remain effective to the extent legally permitted, and the invalid provision will be interpreted as closely as legally permitted to its intended purpose.

Relationship of the Parties

The parties are independent contractors. This Agreement does not create a partnership, joint venture, employment relationship, or agency relationship.

Entire Agreement and Order of Precedence

This Agreement, the subscription selections accepted during Checkout, and documents expressly incorporated into this Agreement constitute the agreement between the parties concerning the Service. If Checkout and this Agreement conflict regarding the selected subscription’s price, billing interval, or quantity, the specific subscription information affirmatively accepted during Checkout controls for that subscription. This Agreement controls other general terms unless the parties expressly agree otherwise in writing.
`;

export default function TermsPage() {
  return <LegalLayout title="MANAGELIFT B2B SAAS CUSTOMER AGREEMENT" effectiveDate="August 13, 2026">
    <p><strong>Terms URL:</strong> <a href="/terms">/terms</a></p>
    <p><strong>Privacy Notice URL:</strong> <a href="/privacy">/privacy</a></p>
    <p><strong>Seller:</strong> ManageLift LLC — state of formation: Indiana; legal notice address: 4335 Stone Mill Dr., Indianapolis, IN 46237</p>
    <LegalText text={TERMS_TEXT} />
  </LegalLayout>;
}
